import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { invalidateIfNotInFlight } from '@/patients/lib/invalidate-if-not-in-flight';
import { formatLocalDatetimeForWire } from '@/shared/lib/date-format';
import type { SignConsentBody } from '@/consent/types/consent-api';

/**
 * POST /api/Consent/SignConsent → boolean.
 *
 * Contract: Scrips.Patient `ConsentController.SignConsent` /
 * `SignConsentRequest` (PR #882). Applies one or more signatures
 * (multi-signer: patient + guardian + witness) and activates the consent.
 * The signed PDF (rendered + PAdES-signed server-side, CG-B3/B4) is referenced
 * via `signedDocumentId` / `signedPdfUrl`.
 *
 * Signatures arrive from a resolved `SignatureCaptureProvider` (CG-F1/F2):
 * each signer carries `signaturePng` (always) + `biometricStrokes` (canvas
 * only) + `capabilityCode`. Per-signer `signedAt` and top-level `capturedAt`
 * ship no-Z.
 *
 * Cache: invalidates the record, the patient list, and the audit trail so the
 * activated status + new Provenance rows surface immediately.
 */
export function useSignConsent(patientId?: string | null) {
  const qc = useQueryClient();
  return useMutation<boolean, Error, SignConsentBody>({
    mutationFn: (body) =>
      post<boolean>('/api/Consent/SignConsent', {
        ...body,
        capturedAt: body.capturedAt
          ? formatLocalDatetimeForWire(body.capturedAt)
          : null,
        signers: body.signers.map((s) => ({
          ...s,
          signedAt: s.signedAt ? formatLocalDatetimeForWire(s.signedAt) : null,
        })),
      }),
    onSuccess: (_ok, { consentRecordId }) => {
      invalidateIfNotInFlight(qc, ['consent', consentRecordId]);
      invalidateIfNotInFlight(qc, ['consent-audit', consentRecordId]);
      if (patientId) invalidateIfNotInFlight(qc, ['patient-consents', patientId]);
    },
  });
}
